<template>
  <div v-loading="dataLoading" class="app-content-wrapper">
    <div class="search-wrapper">
      <search-component
        ref="search"
        class="dispute-search"
        :search-condition="searchCondition"
        :fuzzySearchPlaceholder="fuzzySearchPlaceholder"
        :showMerchantSearch="true"
        router-name="/transaction/dispute"
      >
      </search-component>
    </div>
    <div class="table-wrapper">
      <custom-table
        :table-set="tableCondition"
        :service-function="queryList"
        ref="table"
        :first-parameters="getWeekParams('startTime','endTime')"
        :is-first-automatic-visit="false"
        :showMerchantNo="true"
        router-name="/transaction/dispute"
      >
      </custom-table>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import CustomTable from '@/components/table/custom-table.vue';
import SearchComponent from '@/components/table/search-component.vue';
import GlobalData from '@/utils/global/index';
import {
  getEnantiomericValue, initSelectedData,
} from '@/utils/tools/constant';
import { getWeekParams, initWeek } from '@/utils/tools/time';
import DisputeService from '@/views/transaction/api/dispute.service';
import {
  OnlineRefundStatus,
  DisputeAuditStatus,
} from '@/views/transaction/status/transaction';

export default {
  components: {
    SearchComponent,
    CustomTable,
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
  },
  data() {
    return {
      dataLoading: false,
      fuzzySearchPlaceholder: `${this.$t(
        'message.tableCommon.txnSerialNumber',
      )};${this.$t('message.tableCommon.originalTxnSerialNumber')};${this.$t(
        'message.tableCommon.merchantOrderNumber',
      )}`,
      searchCondition: [
        {
          key: 'auditStatus',
          name: 'message.tableCommon.auditStatus',
          type: 'select',
          defValue: '',
          isInternationalization: true,
          options: DisputeAuditStatus,
        },
        {
          key: 'returnStatus',
          name: 'message.tableCommon.onlineRefundStatus',
          type: 'select',
          defValue: '',
          isInternationalization: true,
          options: OnlineRefundStatus,
        },
        {
          key: ['startTime', 'endTime'],
          name: 'message.tableCommon.creationTime',
          defValue: initWeek(),
          type: 'date_time',
        },
      ],
      tableCondition: [
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.originalTxnSerialNumber',
          },
          key: 'originalTxnId',
          type: 'text',
          itemWidth: 240,
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.merchantOrderNumber',
          },
          key: 'merchantTxnId',
          type: 'text',
          itemWidth: 240,
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.refundSerialNumber',
          },
          key: 'refundTxnId',
          type: 'text',
          itemWidth: 240,
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.email',
          },
          key: 'accountEmail',
          type: 'text',
        },
        {
          titleBar: { type: 'text', title: 'message.tableCommon.orderAmount' },
          key: 'orderAmount',
          type: 'text',
          textFunc: (data) => `${data.orderAmount}${data.orderCurrency}`,
          itemStyle: () => {
            return 'font-family: Avenir-Heavy, Avenir;font-weight: 800;line-height: 28px;';
          },
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.dispute.disputeReason',
          },
          key: 'disputeReason',
          type: 'text',
          textFunc: (data) =>
            getEnantiomericValue(this.disputeReasonList, data.disputeReason),
        },
        { // 退款金额
          titleBar: { type: 'text', title: 'message.tableCommon.refundAmount' },
          key: 'refundAmount',
          type: 'text',
          textFunc: (data) => `${data.refundAmount}${data.orderCurrency}`,
          itemStyle: () => {
            return 'font-family: Avenir-Heavy, Avenir;font-weight: 800;line-height: 28px;';
          },
        },
        {
          titleBar: { type: 'text', title: 'message.tableCommon.auditStatus' },
          key: 'auditStatus',
          type: 'text',
          isInternationalization: true,
          textFunc: (data) =>
            getEnantiomericValue(DisputeAuditStatus, data.auditStatus),
          itemStyle: (data) => {
            if (+data.auditStatus === 0) {
              return 'background:rgba(255,178,9,0.15);padding:2px 8px;border-radius:4px;';
            } else if (+data.auditStatus === 1) {
              return 'background:rgba(0,221,40,0.15);padding:2px 8px;border-radius:4px;';
            }
            return 'background:rgba(252,100,45,0.15);padding:2px 8px;border-radius:4px;';
          },
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.onlineRefundStatus',
          },
          key: 'refundStatus',
          type: 'text',
          isInternationalization: true,
          textFunc: (data) => {
            if (!data.refundStatus) {
              return 'message.status.pending';
            }
            return getEnantiomericValue(OnlineRefundStatus, data.refundStatus);
          },
          itemStyle: (data) => {
            if (data.refundStatus === 'I') { // 不存在
              return '';
            }
            if (data.refundStatus === 'F') {
              return 'background:rgba(252,100,45,0.15);padding:2px 8px;border-radius:4px;';
            } else if (data.refundStatus === 'S') {
              return 'background:rgba(0,221,40,0.15);padding:2px 8px;border-radius:4px;';
            }
            return 'background:rgba(255,178,9,0.15);padding:2px 8px;border-radius:4px;';
          },
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.creationTime',
          },
          key: 'createTime',
          type: 'text',
        },
        {
          titleBar: {
            type: 'text',
            title: 'message.tableCommon.auditTime',
          },
          key: 'auditTime',
          type: 'text',
        },
        {
          key: 'buttons',
          titleBar: {
            title: 'message.tableCommon.operation',
            type: 'operations',
          },
          type: 'operations',
          subTableDisplays: [
            {
              type: 'text_button',
              textFunc: () => 'message.tableCommon.audit',
              // permissionCode: 'transaction:dispute:audit',
              isDisplayFunc: (data) => `${data.auditStatus}` === DisputeAuditStatus[1].value && this.app.userLogin.merchantType !== '3',
              func: (data) => {
                this.$router.push({
                  path: '/transaction/dispute/detail',
                  query: {
                    flag: '0',
                    disputeId: data.disputeId,
                    originalTxnId: data.originalTxnId,
                  },
                });
              },
            },
            {
              type: 'text_button',
              textFunc: () => 'message.tableCommon.see',
              // permissionCode: 'transaction:dispute:see',
              func: (data) => {
                this.$router.push({
                  path: '/transaction/dispute/detail',
                  query: {
                    flag: '1',
                    disputeId: data.disputeId,
                    originalTxnId: data.originalTxnId,
                  },
                });
              },
            },
          ],
        },
      ],
      disputeReasonList: [],
    };
  },
  mounted() {
    this.initMethod();
  },
  methods: {
    ...DisputeService,
    getWeekParams,
    async initMethod() {
      this.dataLoading = true;
      try {
        const dict = await GlobalData.getDictList(['DISPUTE_REASON']);
        this.disputeReasonList = initSelectedData(dict.DISPUTE_REASON || []);
        if (this.$refs.table) {
          this.$refs.table.firstVisitHttp();
        }
      } catch (err) {
        //
      }
      this.dataLoading = false;
    },
  },
};
</script>
<style lang="scss">
html[lang="en"] .dispute-search .fuzzy-input-box {
  width: 536px !important;
}
</style>
